import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DataService } from '@app-core/services/data/data.service';
import { TranslateService } from '@ngx-translate/core';
import { configEvents } from '@app-asset-config/models/tagging.model';
import { StorageService } from '@app-core/services/storage/storage.service';
import { VIDEO_TYPE_LIST } from '@app-core/constants/constants';
import { EVENTS_CONFIG } from '@app-core/constants/constants';
import { MixPanelService } from '@app-core/services/mixpanel/mix-panel.service';
import { getClientConfig } from '@config/client-config';
@Component({
  selector: 'app-auto-coaching-event-selection',
  templateUrl: './auto-coaching-event-selection.component.html',
  styleUrls: ['./auto-coaching-event-selection.component.scss'],
})
export class AutoCoachingEventSelectionComponent implements OnInit {
  public fleetId: string;
  public eventsConfig: configEvents[];
  public othersConfig: configEvents[];
  public customEvents: configEvents[];
  public valuesChanged: boolean = false;
  private previousFormValue: {
    enableAutoSelection: boolean;
    maxAutoSelectedEvents: number;
  };

  @Input()
  public coachConfigLoader: boolean = true;
  @Input()
  public enableAutoSelection: boolean;
  @Input()
  public maxAutoSelectedEvents: number;
  @Input()
  public autoCoachingLoader: boolean = false;
  @Input()
  public enableSelfCoaching: boolean;
  @Input()
  public enableInPersonEscalation: boolean;

  @Output()
  public autoCoachingValues = new EventEmitter<any>();
  public autoCoachingThresholdForm = new FormGroup({
    enableAutoSelection: new FormControl(false),
    maxAutoSelectedEvents: new FormControl(null, [Validators.required, Validators.min(1), Validators.max(100), this.numberValidator]),
  });

  constructor(
    public dataService: DataService,
    public translate: TranslateService,
    private storageService: StorageService,
    private mixpanelService: MixPanelService
  ) {}

  public ngOnInit(): void {
    const combinedEventsList = this.dataService.modifyFleeEvents();
    const modifiedEventsConfig = this.dataService.transformObject(combinedEventsList);
    const filteredCustomData = {};
    const filterStandardEvents = {};
    for (const key in modifiedEventsConfig) {
      if (!(key in EVENTS_CONFIG)) {
        filteredCustomData[key] = modifiedEventsConfig[key];
      } else {
        filterStandardEvents[key] = modifiedEventsConfig[key];
      }
    }

    const otherConfigList = [
      ...VIDEO_TYPE_LIST,
      {
        label: getClientConfig().configuration.externalEventsLabel,
        value: 'externalEvents',
      },
    ].filter((obj) => obj.label !== 'Event On-Demand');
    this.othersConfig = this.processConfig(otherConfigList);
    this.eventsConfig = this.processConfig(filterStandardEvents);
    this.customEvents = this.processConfig(filteredCustomData);

    this.updateData();
    this.formValueChanges();
  }

  public ngOnChanges(): void {
    this.updateData();
  }

  public openFaq() {
    this.mixpanelService.track('link-clicked/configurations-page/coaching-page/automated-triage-card/how-does-automated-triage-work');
    this.dataService?.openFaq('AutomatedCoaching');
  }

  public updateData() {
    if (this.enableAutoSelection !== undefined && this.maxAutoSelectedEvents !== undefined) {
      this.autoCoachingThresholdForm.setValue(
        {
          enableAutoSelection: this.enableAutoSelection,
          maxAutoSelectedEvents: this.maxAutoSelectedEvents,
        },
        { emitEvent: false }
      );

      this.previousFormValue = {
        enableAutoSelection: this.enableAutoSelection,
        maxAutoSelectedEvents: this.maxAutoSelectedEvents,
      };

      this.enableAutoSelection
        ? this.autoCoachingThresholdForm.get('maxAutoSelectedEvents')?.enable()
        : this.autoCoachingThresholdForm.get('maxAutoSelectedEvents')?.disable();
    }
  }

  public toggleChanged(event: any) {
    if (event?.checked) {
      this.autoCoachingThresholdForm.get('maxAutoSelectedEvents')?.enable();
    } else {
      this.autoCoachingThresholdForm.get('maxAutoSelectedEvents')?.setValue(this.maxAutoSelectedEvents);
      this.autoCoachingThresholdForm.get('maxAutoSelectedEvents')?.disable();
    }
  }

  public formValueChanges() {
    this.autoCoachingThresholdForm.valueChanges.subscribe(() => {
      const enableAutoSelection = this.autoCoachingThresholdForm.get('enableAutoSelection').value;
      const maxAutoSelectedEvents = this.autoCoachingThresholdForm.get('maxAutoSelectedEvents').value;
      this.valuesChanged = enableAutoSelection !== this.enableAutoSelection || maxAutoSelectedEvents !== this.maxAutoSelectedEvents;

      if (enableAutoSelection !== this.previousFormValue.enableAutoSelection) {
        this.mixpanelService.track('toggle-switched/configurations-page/coaching-page/automated-triage-card/auto-triage-toggle', {
          AutoSelectionEnabled: enableAutoSelection,
        });
      }

      if (maxAutoSelectedEvents !== this.previousFormValue.maxAutoSelectedEvents) {
        this.mixpanelService.track('input-changed/configurations-page/coaching-page/automated-triage-card/maximum-events-per-session', {
          MaxAutoSelectedEvents: maxAutoSelectedEvents,
        });
      }

      this.previousFormValue = {
        enableAutoSelection,
        maxAutoSelectedEvents,
      };
    });
  }

  public onSubmit() {
    this.mixpanelService.track('button-clicked/configurations-page/coaching-page/automated-triage-card/submit');
    const coachingConfig = this.storageService.getStorageValue('coachingConfig');
    const key = this.getEvents(this.eventsConfig, this.othersConfig, this.customEvents);
    const selectedKey = this.getSelectedKeys(coachingConfig?.eventTypes, key);
    this.mixpanelService.track('Submit Automated Coaching - Coaching Tab / Configuration page', {
      'Change Values': this.autoCoachingThresholdForm.value,
    });
    const body = {
      ...this.autoCoachingThresholdForm.value,
      eventTypes: selectedKey,
      coachingInterval: coachingConfig?.coachingInterval,
      incidentsPer100Miles: coachingConfig?.incidentsPer100Miles,
      minimumEventThreshold: coachingConfig?.minimumEventThreshold,
      enableSelfCoaching: this.enableSelfCoaching,
      enableInPersonEscalation: this.enableInPersonEscalation,
    };
    this.autoCoachingValues.emit(body);
  }

  private processConfig(configObject): configEvents[] {
    return Object.entries(configObject)
      .map(([key, value]: [string, configEvents]) => ({
        key,
        checked: false,
        ...value,
      }))
      .sort((a, b) => (a.label > b.label ? 1 : -1));
  }

  public getEvents(eventsConfigs: any, otherConfig: any, customevents: any): string[] {
    const standarkEventKeys = Object.entries(eventsConfigs).map(([_key, value]: [string, configEvents]) => value.key);
    const otherConfigKeys = Object.entries(otherConfig).map(([_key, value]: [string, configEvents]) =>
      value.key === '0' ? 'panic-button' : ''
    );
    const customEventsKeys = Object.entries(customevents).map(([_key, value]: [string, configEvents]) => value.key);
    return [...standarkEventKeys, ...otherConfigKeys, ...customEventsKeys];
  }

  private getSelectedKeys(storedArray: any[], autocompleted: any[]): string[] {
    const includedKeys: string[] = [];
    autocompleted.forEach((value) => {
      if (storedArray.includes(value)) {
        includedKeys.push(value);
      }
    });
    return includedKeys;
  }

  private numberValidator(control: FormControl) {
    const value = control.value;
    if (!/^\d+$/.test(value)) {
      return { notANumber: true };
    }
    return null;
  }
}
